import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { isNil, isString, map } from 'lodash-es';
import { Template, TemplateScopeType, TemplateType } from '@dev/ee-api/ee-api-models';
import { ArrowDropDownIcon, SimplePopover, BasicInput, palette, Typography } from '@dev/ee-components';

import i18n from '@/../i18n';
import { getLandingPageList, StartLoadingTemplates, StartLoadingTemplateParams } from '@/store/landingPageList/actions';
import { debounce } from '@/services/tools/debounce';
import { PageTypeEnum } from '@/store/landingPageList/types';
import { getApiPath } from '@/services/Secuirty';

import { ArrowDropdownContainer, LandingPageWrapper, ListOption, ListOptionImage } from './styles';
import { ModalStateAction, setDoubleOptInPageValueAction, setLandingPageSearchInputValueAction } from '../../state';

const OPTIONS_PAGE_COUNT = 10;

interface Option { 
    label: string,
    value: string,
    link?: string,
    image?: string,
}

const getOptions = (templates: Template[], search?: string): Option[] => {
    if (!templates.length && isString(search)) {
        return ([{
            label: i18n.t('noresults'),
            image: null,
            value: "noresults",
        }]);
    }

    if (!templates.length) {
        return [];
    }

    return map(templates, template => ({
        image: template.Thumbnail,
        label: template.Name,
        value: template.Name,
        link: template.Link
    }));
}

const getOptionBody = (el: Record<string, string | number | boolean>): JSX.Element => {
    const option = el as unknown as Option;
    return (
        <ListOption>
            {option?.image &&
                <ListOptionImage><img src={`${getApiPath()}/userfile${encodeURI(option?.image)}`} /></ListOptionImage>
            }
            <Typography variant='body1'>{option.label}</Typography>
        </ListOption>
    );
};

const InputEndIcon = <ArrowDropdownContainer><ArrowDropDownIcon color={palette.indigo[6]} /></ArrowDropdownContainer>;

interface FormMultiDoubleOptInLandingPageProps {
    doubleOptInPageValue?: string,
    searchInput?: string,
    dispatchModalState: React.Dispatch<ModalStateAction<any>>,
}

export const FormMultiDoubleOptInLandingPage = ({ doubleOptInPageValue, searchInput, dispatchModalState }: FormMultiDoubleOptInLandingPageProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
        
    const [searchParams, setSearchParams] = useState<StartLoadingTemplateParams>({
        search: '',
        page: 1,
        limit: OPTIONS_PAGE_COUNT,
        type: [TemplateScopeType.Personal],
        templateTypes: [TemplateType.LandingPageEditor,TemplateType.LandingPageTemplate],
        isInfinityScroll: true,
        pageType: PageTypeEnum.DoubleOptIn,
    });
    
    const { templatesList, loadingTemplates: loading, hasMoreTemplates: hasMore } = useSelector(getLandingPageList);
    const landingPageList = getOptions(templatesList, searchInput);
    
    const inputWidth = document.getElementById("double-opt-in-landing-dropdown-wrapper")?.offsetWidth || 899;

    useEffect((): void => {
        dispatch(StartLoadingTemplates(searchParams));
    },[]);

    const updateTemplates = (search: string, customOffset?: number) => {
        if (isString(search) && search.length === 0) {
            return;
        }
        const offset = customOffset ?? templatesList?.length;
        const params: StartLoadingTemplateParams = { 
            ...searchParams, 
            search,
            page: Math.floor(offset/OPTIONS_PAGE_COUNT) + 1
        };
        setSearchParams(params);
        if(hasMore || !offset) { 
            dispatch(StartLoadingTemplates(params));
        }
    };

    const onLandingInputChange = (e): void => {
        const value = e?.target?.value || '';
        dispatchModalState(setLandingPageSearchInputValueAction(value));
        debounce((): void => {
            updateTemplates(value, 0);
        });
    };

    const onClickLandingPageDropdown = (e, option): void => {
        if (option.value === "noresults") return;
        dispatchModalState(setDoubleOptInPageValueAction(option.value, option.link));

        // RESET SEARCH:
        isString(searchInput) && updateTemplates(null, 0);
    }

    const inputValue = searchInput ?? (!isNil(doubleOptInPageValue) ? doubleOptInPageValue : landingPageList?.[0]?.label) ?? '';

    return (
        <LandingPageWrapper id="double-opt-in-landing-dropdown-wrapper">
            <SimplePopover
                id='double-opt-in-checkout-landing-page-dropdown'
                type="custom"
                disableSearch
                onItemClick={onClickLandingPageDropdown}
                placement="bottom"
                maxHeight={450}
                listItemWidth={inputWidth}
                options={landingPageList as any}
                rowsPerChunk={OPTIONS_PAGE_COUNT}
                cachedListSize={OPTIONS_PAGE_COUNT}
                infiniteScrollProps = {{
                    dataLength: landingPageList.length || OPTIONS_PAGE_COUNT,
                    hasMore: hasMore ?? true,
                    next: () => updateTemplates(searchInput),
                }}
                customDropdownBody={getOptionBody}
                isLoadingOptions={loading}
            >
                <BasicInput
                    id='double-opt-in-checkout-landing-page-input'
                    label={t('landingpage')}
                    value={inputValue}
                    onChange={onLandingInputChange}
                    endIcon={InputEndIcon}
                />
            </SimplePopover>
        </LandingPageWrapper>
    );
};
